import { Alarm, ThresholdMode } from '../gql/generated';
import { logger } from './logger';

/**
 * Grafana `var-thresholdMode` values: 0 = AboveHigh, 1 = BelowLow, 2 = OutsideRange
 */
const thresholdModeParam: Record<ThresholdMode, string> = {
  [ThresholdMode.AboveHigh]: '0',
  [ThresholdMode.BelowLow]: '1',
  [ThresholdMode.OutsideRange]: '2',
};

const MINUTE_MS = 60 * 1000;
const DEFAULT_PREVIEW_RANGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const roundUpToNearestMinute = (durationMs: number): number =>
  Math.ceil(durationMs / MINUTE_MS) * MINUTE_MS;

/**
 * Builds the Grafana "Pulse machine alert view" dashboard URL for a given alarm.
 *
 * If the alarm has been triggered before, the time range is its triggered/resolved
 * window with a 5% buffer added on both ends (rounded up to the nearest minute) so
 * the chart boundaries don't fall directly on the alarm's triggered/resolved
 * timestamps. If the alarm has never triggered (e.g. it was just created), the
 * range instead defaults to the last 24 hours so a preview is still available.
 *
 * Returns `undefined` only if the dashboard URL isn't configured.
 */
export function buildAlarmDetailUrl(alarm: Alarm): string | undefined {
  const baseUrl = import.meta.env.VITE_ALARM_DETAIL_URL;
  if (!baseUrl) {
    logger.error(
      'VITE_ALARM_DETAIL_URL is not configured in environment variables'
    );
    return undefined;
  }

  const machineIds = (alarm.machines || [])
    .map((machine) => machine.machineId)
    .filter((machineId): machineId is string => Boolean(machineId));

  const nowMs = Date.now();
  let fromMs: number;
  let toMs: number;

  if (alarm.lastTriggeredAt) {
    const triggeredMs = new Date(alarm.lastTriggeredAt).getTime();
    if (Number.isNaN(triggeredMs)) {
      fromMs = nowMs - DEFAULT_PREVIEW_RANGE_MS;
      toMs = nowMs;
    } else {
      const resolvedCandidateMs = alarm.lastResolvedAt
        ? new Date(alarm.lastResolvedAt).getTime()
        : NaN;
      const resolvedMs =
        !Number.isNaN(resolvedCandidateMs) && resolvedCandidateMs > triggeredMs
          ? resolvedCandidateMs
          : nowMs;

      const rangeBufferMs = roundUpToNearestMinute(
        0.05 * (resolvedMs - triggeredMs)
      );

      fromMs = triggeredMs - rangeBufferMs;
      toMs = resolvedMs < nowMs ? resolvedMs + rangeBufferMs : nowMs;
    }
  } else {
    // Never triggered (e.g. a newly created alarm) - fall back to a recent
    // default window so a preview is still shown instead of disabling it.
    fromMs = nowMs - DEFAULT_PREVIEW_RANGE_MS;
    toMs = nowMs;
  }

  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch (error) {
    logger.error('VITE_ALARM_DETAIL_URL is not a valid absolute URL', error);
    return undefined;
  }

  machineIds.forEach((machineId) =>
    url.searchParams.append('var-machineIds', machineId)
  );
  url.searchParams.set('var-metricCategory', alarm.signalCategory);
  url.searchParams.set('var-metric', alarm.signal);
  url.searchParams.set('var-thresholdLow', String(alarm.thresholdLow ?? 0));
  url.searchParams.set('var-thresholdHigh', String(alarm.thresholdHigh ?? 0));
  url.searchParams.set('var-thresholdMode', thresholdModeParam[alarm.thresholdMode]);
  url.searchParams.set('from', String(fromMs));
  url.searchParams.set('to', String(toMs));
  url.searchParams.set('timezone', 'browser');

  return url.toString();
}

/**
 * Opens the Grafana alarm detail dashboard for the given alarm in a new tab.
 */
export function openAlarmDetail(alarm: Alarm): void {
  const url = buildAlarmDetailUrl(alarm);
  if (!url) return;

  window.open(url, '_blank', 'noopener,noreferrer');
}
